import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { Family, Profile, SessionStatus } from '../types';

type AuthState = {
  status: SessionStatus;
  user: User | null;
  profile: Profile | null;
  family: Family | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [family, setFamily] = useState<Family | null>(null);
  const [status, setStatus] = useState<SessionStatus>('loading');

  async function loadProfileAndFamily(currentUser: User | null) {
    if (!currentUser) {
      setProfile(null);
      setFamily(null);
      setStatus('signed_out');
      return;
    }

    const { data: prof, error: profErr } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', currentUser.id)
      .maybeSingle();

    if (profErr) console.warn('[auth] profile load error:', profErr.message);

    if (!prof || !prof.family_id) {
      setProfile(prof ?? null);
      setFamily(null);
      setStatus('no_family');
      return;
    }

    setProfile(prof as Profile);

    const { data: fam, error: famErr } = await supabase
      .from('families')
      .select('*')
      .eq('id', prof.family_id)
      .maybeSingle();

    if (famErr) console.warn('[auth] family load error:', famErr.message);

    setFamily((fam as Family) ?? null);
    setStatus(fam ? 'ready' : 'no_family');
  }

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }: { data: { session: Session | null } }) => {
      if (!active) return;
      const u = data.session?.user ?? null;
      setUser(u);
      void loadProfileAndFamily(u);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      const u = session?.user ?? null;
      setUser(u);
      void loadProfileAndFamily(u);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const refresh = async () => {
    await loadProfileAndFamily(user);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ status, user, profile, family, refresh, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useSession(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useSession must be used inside <AuthProvider>');
  return ctx;
}
